import {
  getStateFromPath,
  type NavigationState,
  type PartialState,
} from '@react-navigation/native';

/** URL path pattern for Evaluation Desk deep links. */
export const EVALUATION_DESK_LINK_PATH = 'lecturer/evaluations/:moduleId?/:userId?/:courseId?';

export type EvaluationDeskFlatLinkParams = {
  moduleId?: string;
  userId?: string;
  courseId?: string;
};

export type EvaluationDeskNestedParams = {
  module?: { id: string; title?: string };
  submission?: {
    userId: string;
    moduleId: string;
    courseId: string;
    [key: string]: unknown;
  };
};

/**
 * Maps flat URL segments to nested EvaluationDesk route params.
 */
export function flatToNestedEvaluationDeskParams(
  flat: EvaluationDeskFlatLinkParams,
): EvaluationDeskNestedParams {
  const { moduleId, userId, courseId } = flat;
  if (!moduleId) return {};
  const result: EvaluationDeskNestedParams = {
    module: { id: moduleId, title: '' },
  };
  if (userId && courseId) {
    result.submission = { userId, moduleId, courseId };
  }
  return result;
}

/**
 * Maps nested EvaluationDesk route params to flat URL segments.
 */
export function nestedToFlatEvaluationDeskParams(
  params?: EvaluationDeskNestedParams | EvaluationDeskFlatLinkParams,
): EvaluationDeskFlatLinkParams {
  if (!params) return {};
  if ('moduleId' in params && params.moduleId && !('module' in params && params.module)) {
    return {
      moduleId: params.moduleId,
      userId: params.userId,
      courseId: params.courseId,
    };
  }
  const nested = params as EvaluationDeskNestedParams;
  const moduleId = nested.module?.id ?? nested.submission?.moduleId;
  return {
    moduleId,
    userId: nested.submission?.userId,
    courseId: nested.submission?.courseId,
  };
}

type NavState = NavigationState | PartialState<NavigationState>;

/**
 * After getStateFromPath: convert flat link params to nested route params.
 */
export function patchEvaluationDeskParamsInState<T extends NavState | undefined>(
  state: T,
): T {
  if (!state?.routes) return state;
  const routes = state.routes.map((route) => {
    if (route.name === 'EvaluationDesk' && route.params) {
      const flat = route.params as EvaluationDeskFlatLinkParams;
      if (flat.moduleId && !('module' in (flat as object))) {
        return {
          ...route,
          params: flatToNestedEvaluationDeskParams(flat),
        };
      }
    }
    if (route.state) {
      return {
        ...route,
        state: patchEvaluationDeskParamsInState(route.state),
      };
    }
    return route;
  });
  return { ...state, routes } as T;
}

/**
 * Before getPathFromState: flatten nested route params to link segments.
 */
export function flattenEvaluationDeskParamsInState<T extends NavState>(
  state: T,
): T {
  if (!state.routes) return state;
  const routes = state.routes.map((route) => {
    if (route.name === 'EvaluationDesk' && route.params) {
      const nested = route.params as EvaluationDeskNestedParams;
      if (nested.module || nested.submission) {
        return {
          ...route,
          params: nestedToFlatEvaluationDeskParams(nested),
        };
      }
    }
    if (route.state) {
      return {
        ...route,
        state: flattenEvaluationDeskParamsInState(route.state),
      };
    }
    return route;
  });
  return { ...state, routes } as T;
}

/** Linking screen config fragment shared with production and tests. */
export const evaluationDeskLinkingScreenConfig = {
  path: EVALUATION_DESK_LINK_PATH,
  parse: {
    moduleId: String,
    userId: String,
    courseId: String,
  },
};

/** Minimal linking config for EvaluationDesk path round-trip tests. */
export const evaluationDeskLinkingConfig = {
  screens: {
    EvaluationDesk: evaluationDeskLinkingScreenConfig,
  },
};

/**
 * Production URL → state parser. Nest EvaluationDesk IDs the same way as
 * getPathFromState flatten, so browser restore / Forward matches the address bar.
 */
export function getEvaluationDeskStateFromPath(
  path: string,
  options?: Parameters<typeof getStateFromPath>[1],
) {
  return patchEvaluationDeskParamsInState(getStateFromPath(path, options));
}
